import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Avatar,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    Paper,
    IconButton,
    OutlinedInput,
    SelectChangeEvent,
    Checkbox,
    ListItemText
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import SportsIcon from '@mui/icons-material/Sports';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { 
    getCategoriesBySports, 
    createFlashBetCategory, 
    updateFlashBetCategory, 
    deleteFlashBetCategory,
    getFlashBetSports 
} from '../Api';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import CreateCategoryRequest from '../models/dtos/CreateCategoryRequest';
import UpdateCategoryRequest from '../models/dtos/UpdateCategoryRequest';
import CategoryEditModal from '../components/CategoryEditModal';
import { GridCheckCircleIcon, GridCheckIcon } from '@mui/x-data-grid';
import { SportsFootballOutlined, SportsSoccerOutlined } from '@mui/icons-material';

const FlashBetCategoriesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Query parameter management
    const getQueryParams = () => {
        const params = new URLSearchParams(location.search);
        const sportIds = params.getAll('sportIds').map(Number).filter(id => !isNaN(id));
        const showOnlyEnabled = params.get('showOnlyEnabled') === 'true';
        const search = params.get('search') || '';
        return { sportIds, showOnlyEnabled, search };
    };

    const updateQueryParams = (params: { sportIds: number[], showOnlyEnabled: boolean, search: string }) => {
        const urlParams = new URLSearchParams();
        params.sportIds.forEach(id => urlParams.append('sportIds', id.toString()));
        if (params.showOnlyEnabled) urlParams.set('showOnlyEnabled', 'true');
        if (params.search) urlParams.set('search', params.search);
        
        navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
    };

    const queryParams = getQueryParams();
    
    // State management
    const [allSports, setAllSports] = useState<FlashBetSportDto[]>([]);
    const [allCategories, setAllCategories] = useState<FlashBetCategoryDto[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<FlashBetCategoryDto[]>([]);
    const [selectedSportIds, setSelectedSportIds] = useState<number[]>(queryParams.sportIds);
    const [showOnlyEnabled, setShowOnlyEnabled] = useState<boolean>(queryParams.showOnlyEnabled);
    const [searchTerm, setSearchTerm] = useState<string>(queryParams.search);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
    
    // Modal states
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<FlashBetCategoryDto | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [categoryToDelete, setCategoryToDelete] = useState<FlashBetCategoryDto | null>(null);

    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    // Update query parameters when filters change
    useEffect(() => {
        updateQueryParams({ 
            sportIds: selectedSportIds, 
            showOnlyEnabled, 
            search: searchTerm 
        });
    }, [selectedSportIds, showOnlyEnabled, searchTerm]);

    // Load categories when filters change
    useEffect(() => {
        loadCategories();
    }, [selectedSportIds]);

    // Apply client-side filtering when search or toggle changes
    useEffect(() => {
        applyClientSideFiltering();
    }, [allCategories, showOnlyEnabled, searchTerm]);

    // Cleanup search timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const loadInitialData = async () => {
        try {
            const sportsResult = await getFlashBetSports();
            setAllSports(sportsResult);
            
            // If no sports selected in query params, select all by default
            if (queryParams.sportIds.length === 0) {
                const allSportIds = sportsResult.map(sport => sport.id);
                setSelectedSportIds(allSportIds);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        if (selectedSportIds.length === 0) {
            setAllCategories([]);
            return;
        }
        
        setCategoriesLoading(true);
        try {
            const categoriesResult = await getCategoriesBySports(selectedSportIds);
            setAllCategories(categoriesResult);
        } catch (error) {
            console.error('Error loading categories:', error);
            setAllCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const applyClientSideFiltering = () => {
        let filtered = [...allCategories];

        // Filter by enabled status
        if (showOnlyEnabled) {
            filtered = filtered.filter(category => category.enabled !== false);
        }

        // Filter by search term
        if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(category => {
                return category.name.toLowerCase().includes(searchLower) ||
                       category.id.toString().includes(searchLower) ||
                       (category.localizations && Object.values(category.localizations).some(name => 
                           name.toLowerCase().includes(searchLower)
                       ));
            });
        }

        setFilteredCategories(filtered);
    };

    const handleSportsChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value as number[];
        setSelectedSportIds(value);
    };

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyEnabled(event.target.checked);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // Set new timeout for debounced search
        const timeout = setTimeout(() => {
            setSearchTerm(value);
        }, 300);
        
        setSearchTimeout(timeout);
    };

    const handleClearSearch = () => {
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        setSearchTerm('');
    };

    const handleSelectAllSports = () => {
        const availableSportIds = showOnlyEnabled ? 
            allSports.filter(sport => sport.enabled !== false).map(sport => sport.id) :
            allSports.map(sport => sport.id);
        setSelectedSportIds(availableSportIds);
    };

    const handleResetSports = () => {
        setSelectedSportIds([]);
    };

    const isSelectAllSportsDisabled = () => {
        const availableSportIds = showOnlyEnabled ? 
            allSports.filter(sport => sport.enabled !== false).map(sport => sport.id) :
            allSports.map(sport => sport.id);
        return availableSportIds.length === 0 || selectedSportIds.length === availableSportIds.length;
    };

    const isResetSportsDisabled = () => {
        return selectedSportIds.length === 0;
    };

    const getSelectedSportsDisplay = () => {
        if (selectedSportIds.length === 0) return 'None selected';
        return selectedSportIds.map(id => {
            const sport = allSports.find(s => s.id === id);
            return sport?.name || `Sport ${id}`;
        }).join(', ');
    };

    const handleEditCategory = (category: FlashBetCategoryDto) => {
        setSelectedCategory(category);
        setEditModalOpen(true);
    };

    const handleAddCategory = () => {
        setAddModalOpen(true);
    };

    const handleSaveEdit = async (categoryData: UpdateCategoryRequest) => {
        if (!selectedCategory) return;
        
        try {
            const result = await updateFlashBetCategory(selectedCategory.id, categoryData);
            if (result) {
                // Reload categories to refresh the list
                await loadCategories();
                setEditModalOpen(false);
                setSelectedCategory(null);
            }
        } catch (error) {
            console.error('Error updating category:', error);
        }
    };

    const handleSaveAdd = async (categoryData: CreateCategoryRequest | UpdateCategoryRequest) => {
        try {
            // For adding new categories, we need to provide a sportId
            // This should ideally come from a selector in the modal
            if (selectedSportIds.length === 0) {
                console.error('No sport selected for new category');
                return;
            }
            
            const createData: CreateCategoryRequest = 'sportId' in categoryData 
                ? categoryData 
                : {
                    ...categoryData,
                    sportId: selectedSportIds[0] // Use the first selected sport
                };
            
            const result = await createFlashBetCategory(createData);
            if (result) {
                // Reload categories to refresh the list
                await loadCategories();
                setAddModalOpen(false);
            }
        } catch (error) {
            console.error('Error creating category:', error);
        }
    };

    const handleDeleteCategory = (category: FlashBetCategoryDto) => {
        setCategoryToDelete(category);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!categoryToDelete) return;
        
        try {
            const success = await deleteFlashBetCategory(categoryToDelete.id);
            if (success) {
                // Reload categories to refresh the list
                await loadCategories();
            }
        } catch (error) {
            console.error('Error deleting category:', error);
        } finally {
            setDeleteDialogOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setAddModalOpen(false);
        setSelectedCategory(null);
    };

    const handleCopyId = async (categoryId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(categoryId);
            // Could add a toast notification here if needed
        } catch (error) {
            console.error('Failed to copy category ID:', error);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                FlashBet Categories
            </Typography>

            {/* Filters */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3} alignItems="flex-start">
                    {/* Sports Filter */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="sports-select-label">Sports</InputLabel>
                            <Select
                                labelId="sports-select-label"
                                multiple
                                value={selectedSportIds}
                                onChange={handleSportsChange}
                                input={<OutlinedInput label="Sports" />}
                                renderValue={() => getSelectedSportsDisplay()}
                            >
                                {allSports
                                    .filter(sport => !showOnlyEnabled || sport.enabled !== false)
                                    .map((sport) => (
                                        <MenuItem key={sport.id} value={sport.id}>
                                            <Checkbox checked={selectedSportIds.includes(sport.id)} />
                                            <ListItemText primary={sport.name} />
                                            {!sport.enabled && <Chip label="Disabled" size="small" color="warning" />}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        <Box mt={1} display="flex" gap={1}>
                            <Button
                                size="small"
                                onClick={handleSelectAllSports}
                                disabled={isSelectAllSportsDisabled()}
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                onClick={handleResetSports}
                                disabled={isResetSportsDisabled()}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>

                    {/* Search */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Search Categories"
                            placeholder="Search by name, ID, or localization..."
                            defaultValue={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                endAdornment: searchTerm && (
                                    <IconButton onClick={handleClearSearch} edge="end">
                                        <ClearIcon />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>

                    {/* Show Only Enabled Toggle */}
                    <Grid item xs={12} md={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyEnabled}
                                    onChange={handleToggleChange}
                                />
                            }
                            label="Show Only Enabled"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Loading State */}
            {categoriesLoading && (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Categories Grid */}
            <Grid container spacing={3}>
                {filteredCategories.map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={category.id}>
                        <Card 
                            sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                transition: 'all 0.2s',
                                cursor: 'pointer',
                                position: 'relative',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                            onClick={() => {
                                const params = new URLSearchParams();
                                selectedSportIds.forEach(id => params.append('sportIds', id.toString()));
                                params.append('categoryIds', category.id);
                                navigate(`/flashbet/matches?${params.toString()}`);
                            }}
                        >
                            {/* Small Logo URL as icon in top right corner */}
                            {category.smallLogoUrl && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 1
                                    }}
                                >
                                    <Avatar
                                        src={category.smallLogoUrl}
                                        alt="Category Logo"
                                        sx={{ width: 24, height: 24 }}
                                    />
                                </Box>
                            )}
                            
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Avatar
                                        src={category.photoUrl || undefined}
                                        alt={category.name}
                                        sx={{ width: 60, height: 60, mr: 2 }}
                                    />
                                    <Box>
                                        <Typography 
                                            variant="h6" 
                                            component="h2"
                                            sx={{
                                                height: '3rem', // Fixed height for exactly 2 lines
                                                lineHeight: '1.5rem',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                mb: 0.5,
                                                fontWeight: 600
                                            }}
                                        >
                                            {category.name}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontStyle: 'italic',
                                                mb: 0.5,
                                                height: '1rem', // Reserve space for one line
                                                lineHeight: '1rem',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {category.subtitle || ''}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" color="text.secondary">
                                                ID: {category.id}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleCopyId(category.id, e)}
                                                sx={{ 
                                                    padding: '2px',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                }}
                                                title="Copy ID"
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Chip 
                                            label={category.enabled ? 'Enabled' : 'Disabled'}
                                            color={category.enabled ? 'success' : 'default'}
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    </Box>
                                </Box>
                                
                                {/* Statistics Row */}
                                <Box 
                                    display="flex" 
                                    justifyContent="space-between" 
                                    alignItems="center" 
                                    mb={2}
                                    sx={{
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                        p: 1.5
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <CheckCircleIcon fontSize="small" color="success" />
                                        <Typography variant="body2" color="success.main" fontWeight="medium">
                                            {category.activeMatches} active
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <CancelIcon fontSize="small" color="disabled" />
                                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                            {category.disabledMatches} disabled
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <MonetizationOnIcon fontSize="small" color="warning" />
                                        <Typography variant="body2" color="warning.main" fontWeight="medium">
                                            {category.entryFee?.toLocaleString() || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                {category.localizations && Object.keys(category.localizations).length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Localizations:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {Object.entries(category.localizations)
                                                .slice(0, 2)
                                                .map(([lang, name]) => (
                                                    <Chip 
                                                        key={lang}
                                                        label={`${lang}: ${name.length > 15 ? name.substring(0, 15) + '...' : name}`}
                                                        size="small"
                                                        variant="outlined"
                                                        title={`${lang}: ${name}`}
                                                    />
                                                ))}
                                            {Object.keys(category.localizations).length > 2 && (
                                                <Chip
                                                    label={`+${Object.keys(category.localizations).length - 2} more`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                    title={`Additional languages: ${Object.keys(category.localizations).slice(2).join(', ')}`}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                            
                            <CardActions>
                                <Button 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCategory(category);
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button 
                                    size="small" 
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category);
                                    }}
                                >
                                    Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* No Results Message */}
            {!categoriesLoading && filteredCategories.length === 0 && (
                <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="text.secondary">
                        No categories found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {selectedSportIds.length === 0 
                            ? 'Please select at least one sport to view categories.'
                            : 'Try adjusting your filters or search criteria.'}
                    </Typography>
                </Box>
            )}

            {/* Add Button */}
            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={handleAddCategory}
                disabled={selectedSportIds.length === 0}
            >
                <AddIcon />
            </Fab>

            {/* Modals */}
            <CategoryEditModal
                open={editModalOpen}
                category={selectedCategory}
                sportId={selectedSportIds.length > 0 ? selectedSportIds[0] : 0}
                onSave={handleSaveEdit}
                onClose={handleCloseModal}
                isEdit={true}
            />

            <CategoryEditModal
                open={addModalOpen}
                category={null}
                sportId={selectedSportIds.length > 0 ? selectedSportIds[0] : 0}
                onSave={handleSaveAdd}
                onClose={handleCloseModal}
                isEdit={false}
            />

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the category "{categoryToDelete?.name}"? 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default FlashBetCategoriesPage;